import { useCallback, useEffect, useState } from 'react';
import { Confirm, Profile, toast, type Destructive, SignIn } from '@drbell/ui';
import {
  api, ApiError,
  type Invitation, type Me, type OrgDetail, type OrgSummary, type Resident,
} from './api.ts';
import { Residents } from './screens/Residents.tsx';
import { Medicines } from './screens/Medicines.tsx';
import { Handover } from './screens/Handover.tsx';
import { Import } from './screens/Import.tsx';
import { Appointments } from './screens/Appointments.tsx';
import { Staff } from './screens/Staff.tsx';
import { Seats } from './screens/Seats.tsx';
import { Settings } from './screens/Settings.tsx';
import { Rota } from './screens/Rota.tsx';
import { Register } from './screens/Register.tsx';
import { AnnouncementBar, Mark, ReportWrongWordLink, ThemeToggle, useBrandAccent } from '@drbell/ui';

type Tab = 'residents' | 'staff' | 'rota' | 'handover' | 'seats' | 'settings';

export function App() {
  const [me, setMe] = useState<Me | null | 'loading'>('loading');
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  /** Set when a resident's medicines are open, which replaces the tabs. */
  const [medicinesFor, setMedicinesFor] = useState<Resident | null>(null);
  const [importing, setImporting] = useState(false);
  const [appointmentsFor, setAppointmentsFor] = useState<Resident | null>(null);
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [tab, setTab] = useState<Tab>('residents');

  const loadSession = useCallback(() => {
    api
      .get<Me>('/api/me')
      .then((m) => setMe(m.user ? m : null))
      // A 401 is the normal signed-out case, not an error worth showing.
      .catch(() => setMe(null));
  }, []);

  useEffect(loadSession, [loadSession]);

  useEffect(() => {
    if (me === 'loading' || me === null) return;
    api
      .get<{ orgs: OrgSummary[]; invitations?: Invitation[] }>('/api/orgs')
      .then((r) => {
        setOrgs(r.orgs);
        setInvitations(r.invitations ?? []);
        setOrgId((current) => current ?? r.orgs[0]?.id ?? null);
      })
      .catch(() => {
        setOrgs([]);
        setInvitations([]);
      });
  }, [me]);

  const refresh = useCallback(() => {
    if (!orgId) return;
    void api.get<OrgDetail>(`/api/orgs/${orgId}`).then(setDetail).catch(() => setDetail(null));
    // Fetched at the shell rather than per-screen: the staff editor needs the
    // resident list to show a ward scope in names instead of opaque ids.
    void api
      .get<{ patients: Resident[] }>(`/api/orgs/${orgId}/patients`)
      .then((r) => setResidents(r.patients))
      .catch(() => setResidents([]));
  }, [orgId]);

  useEffect(refresh, [refresh]);

  /*
   * The home's own logo and tagline in its header — RX-ORG-11. Re-read on
   * every tab change, so a logo uploaded in Settings shows as soon as the
   * manager moves on.
   */
  const [brand, setBrand] = useState<{
    logoUrl: string | null; tagline: string | null; light: string | null; dark: string | null;
  } | null>(null);
  useEffect(() => {
    if (!orgId) return;
    void api
      .get<{
        logoUrl: string | null;
        branding: { tagline: string | null; primaryColor: string | null; primaryColorDark: string | null };
      }>(`/api/orgs/${orgId}/branding`)
      .then((r) => setBrand({
        logoUrl: r.logoUrl, tagline: r.branding.tagline,
        light: r.branding.primaryColor, dark: r.branding.primaryColorDark,
      }))
      .catch(() => setBrand(null));
  }, [orgId, tab]);
  useBrandAccent(brand?.light, brand?.dark);

  if (me === 'loading') return <p className="p-8 text-(--muted)">Checking session…</p>;
  if (!me) return <SignIn
        title="DrBell for organisations"
        subtitle="Care homes, hospitals and home-care teams."
        onSignedIn={loadSession}
      />;

  if (orgs === null) return <p className="p-8 text-(--muted)">Loading…</p>;

  if (orgs.length === 0) {
    return (
      <NoOrgs
        phone={me.user?.phoneNumber ?? null}
        invitations={invitations}
        onAccepted={loadSession}
      />
    );
  }

  const org = orgs.find((o) => o.id === orgId) ?? orgs[0]!;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* A line from the DrBell team, in the reader's language (docs/08). */}
      <AnnouncementBar surface="org" get={api.get} />
      <header className="mb-6 border-b border-(--line) pb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* The mark, so a nurse on a shared terminal can see at a glance
              which system she is looking at. */}
          <Mark className="h-9 w-9 shrink-0" />
          {brand?.logoUrl && (
            <img src={brand.logoUrl} alt="" className="h-10 max-w-32 shrink-0 object-contain" />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">{org.name}</h1>
            {brand?.tagline && <p className="text-sm">{brand.tagline}</p>}
            <p className="text-sm text-(--muted)">
              {org.type.replace(/_/g, ' ')} · you are {org.role}
              {org.scoped && ' · confined to your assigned residents'}
            </p>
          </div>

          {orgs.length > 1 && (
            <select
              value={org.id}
              onChange={(e) => {
                setOrgId(e.target.value);
                setDetail(null);
                // Otherwise switching homes would leave the previous home's
                // resident open, with their medicines on screen.
                setMedicinesFor(null);
                setAppointmentsFor(null);
              }}
              aria-label="Switch organisation"
              className="ml-auto rounded-lg border border-(--line) bg-(--bg) px-3 py-1.5 text-sm"
            >
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}

          {/*
            Sign out, and it matters more here than anywhere else in the
            product. Care homes run shared ward terminals: one machine, a
            rota of staff, and a session that lasts ninety days. Without
            this the only way for a nurse to end her session is to clear the
            browser's cookies, which she will not do — so the next person to
            sit down is signed in as her, with her ward's residents on
            screen and her name against anything they change.
          */}
          <ThemeToggle className={orgs.length > 1 ? '' : 'ml-auto'} />

          <button
            onClick={() => setProfileOpen(true)}
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Your account
          </button>

          <button
            onClick={() =>
              setPending({
                title: 'Sign out of DrBell on this computer?',
                body: 'This is a shared computer in most homes. Signing out ends the session here only \u2014 it does not affect anybody else\u2019s access or the residents\u2019 reminders.',
                confirmLabel: 'Sign out',
                onConfirm: async () => {
                  await api.post('/api/auth/sign-out');
                  location.reload();
                },
              })
            }
            className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Sign out
          </button>
        </div>

        {org.status !== 'active' && (
          <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
            {org.status === 'pending'
              ? 'This organisation is awaiting approval by DrBell. It cannot hold resident records until it is approved.'
              : 'This organisation is suspended. Contact DrBell.'}
          </p>
        )}
      </header>

      <nav className="mb-6 flex flex-wrap gap-1">
        {([
          { id: 'residents', label: 'Residents' },
          { id: 'staff', label: 'Staff' },
          { id: 'rota', label: 'Rota' },
          { id: 'handover', label: 'Handover' },
          { id: 'seats', label: 'Licences & billing' },
          { id: 'settings', label: 'Settings' },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setMedicinesFor(null);
              setAppointmentsFor(null);
              setImporting(false);
            }}
            aria-current={tab === t.id ? 'page' : undefined}
            className={`min-h-11 rounded-lg px-3 text-sm ${
              tab === t.id ? 'bg-(--accent-soft) font-medium text-(--accent)' : 'hover:bg-(--panel)'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {!detail && <p className="text-(--muted)">Loading…</p>}

      {detail && tab === 'residents' && importing && (
        <Import
          org={org}
          onImported={() => { setImporting(false); refresh(); }}
        />
      )}
      {detail && tab === 'residents' && !importing && appointmentsFor && (
        <Appointments
          resident={appointmentsFor}
          onBack={() => setAppointmentsFor(null)}
        />
      )}
      {detail && tab === 'residents' && !importing && !appointmentsFor && medicinesFor && (
        <Medicines resident={medicinesFor} onBack={() => setMedicinesFor(null)} />
      )}
      {detail && tab === 'residents' && !importing && !medicinesFor && !appointmentsFor && (
        <Residents
          org={org}
          detail={detail}
          onChanged={refresh}
          onOpenMedicines={setMedicinesFor}
          onOpenAppointments={setAppointmentsFor}
          onImport={() => setImporting(true)}
        />
      )}
      {detail && tab === 'staff' && (
        <Staff org={org} detail={detail} residents={residents} onChanged={refresh} />
      )}
      {detail && tab === 'rota' && (
        <Rota org={org} detail={detail} residents={residents} onChanged={refresh} />
      )}
      {detail && tab === 'handover' && <Handover org={org} />}
      {detail && tab === 'seats' && <Seats org={org} />}
      {detail && tab === 'settings' && (
        <Settings
          org={org}
          // The organisation no longer exists: back to the chooser, which
          // re-reads the list and will not offer it again.
          onErased={() => location.reload()}
        />
      )}

      {/* "Report a wrong word", only in a language other than English (docs/31). */}
      {me.user?.locale && me.user.locale !== 'en-IN' && (
        <footer className="mt-10 border-t border-(--line) pt-4 text-right">
          <ReportWrongWordLink api={api} locale={me.user.locale} source="org" />
        </footer>
      )}

      {profileOpen && (
        <Profile api={api} onClose={() => setProfileOpen(false)} onSaved={refresh} />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </div>
  );
}

/**
 * Signed in, but not a member of anything.
 *
 * Two quite different people land here — someone invited who has not accepted,
 * and someone at the wrong address entirely — so it says what to do in both
 * cases rather than picking one and being wrong half the time.
 */
function NoOrgs({ phone, invitations, onAccepted }: {
  phone: string | null;
  invitations: Invitation[];
  onAccepted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);

  if (registering) return <Register onCreated={() => location.reload()} />;

  return (
    <div className="mx-auto mt-24 w-full max-w-md rounded-xl border border-(--line) bg-(--panel) p-6">
      <h1 className="mb-2 text-xl font-semibold">
        {invitations.length > 0 ? 'You have been invited' : 'No organisation yet'}
      </h1>
      <p className="mb-4 text-sm text-(--muted)">
        {invitations.length > 0
          ? `${phone} has been invited to the following. Accepting is what grants access.`
          : `${phone} is signed in, but is not an active member of any organisation.`}
      </p>

      {/*
        The invitations themselves, with a button.
        
        This screen used to tell people to "open the invitation link you were
        sent" — and the endpoint that accepts an invitation had no caller
        anywhere, so there was no link and no other way. An invited nurse
        signed in, was told she belonged to nowhere, and that was the end of
        it: nobody could join a care home except the person who registered it.
      */}
      {invitations.length > 0 && (
        <ul className="mb-5 space-y-2">
          {invitations.map((inv) => (
            <li
              key={inv.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-(--line) bg-(--bg) p-3"
            >
              <div className="min-w-0">
                <div className="font-medium">{inv.name}</div>
                <div className="text-xs text-(--muted)">
                  {inv.type.replace(/_/g, ' ')} · as {inv.role}
                </div>
              </div>
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await api.post(`/api/orgs/${inv.id}/accept`);
                    toast.success(`You have joined ${inv.name}`);
                    onAccepted();
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : String(e));
                    setBusy(false);
                  }
                }}
                className="ml-auto min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
              >
                Accept
              </button>
            </li>
          ))}
        </ul>
      )}

      <ul className="mb-5 space-y-2 text-sm text-(--muted)">
        {invitations.length === 0 && (
        <li>
          <strong className="text-(--fg)">Expecting an invitation?</strong> It
          has to be sent to this number, {phone}. If somebody invited a
          different one, ask them to invite this one instead.
        </li>
        )}
        <li>
          <strong className="text-(--fg)">Looking after a family member?</strong>{' '}
          This console is for care homes and hospitals. Use the DrBell app
          instead — it is where family caregiving lives.
        </li>
      </ul>

      {error && (
        <p role="alert" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      {/* The third person who lands here, and the one who had nowhere to go:
          a care home that followed "For organisations" from the marketing
          site. A signup funnel ending in a dead end is worse than no funnel —
          they arrived because they were interested. */}
      <button
        onClick={() => setRegistering(true)}
        className="mb-3 w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-white"
      >
        Register a new organisation
      </button>

      <button
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          api
            .post('/api/auth/sign-out')
            .then(() => location.reload())
            .catch((e) => {
              setError(e instanceof ApiError ? e.message : String(e));
              setBusy(false);
            });
        }}
        className="w-full rounded-lg border border-(--line) py-2 text-sm hover:border-(--accent)"
      >
        Sign in with a different number
      </button>
    </div>
  );
}
